/**
 * ZeroUnique Google Reviews carousel.
 * - Page-based horizontal scroll (1 / 2 / N cards per view via CSS).
 * - Auto-rotate (data-autoplay = interval in ms, 0 = off), pause on hover/focus/touch.
 * - Prev/next arrows + pagination dots. Controls hidden when everything fits one page.
 */
class ZuReviewsCarousel extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('.zu-reviews__viewport');
    this.track = this.querySelector('.zu-reviews__track');
    this.slides = Array.from(this.querySelectorAll('.zu-reviews__slide'));
    this.dotsWrap = this.querySelector('.zu-reviews__dots');
    this.prevBtn = this.querySelector('.zu-reviews__arrow--prev');
    this.nextBtn = this.querySelector('.zu-reviews__arrow--next');
    this.interval = parseInt(this.dataset.autoplay, 10) || 0;
    this.page = 0;
    this.timer = null;

    if (this.slides.length <= 1) return;

    this.layout();
    this.bind();
    this.start();
  }

  disconnectedCallback() {
    this.stop();
  }

  perView() {
    const slideWidth = this.slides[0].getBoundingClientRect().width;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const n = Math.round((this.viewport.clientWidth + gap) / (slideWidth + gap));
    return Math.max(1, n);
  }

  layout() {
    this.pages = Math.ceil(this.slides.length / this.perView());
    const hasPages = this.pages > 1;
    this.classList.toggle('zu-reviews__carousel--has-pages', hasPages);
    if (this.prevBtn) this.prevBtn.hidden = !hasPages;
    if (this.nextBtn) this.nextBtn.hidden = !hasPages;
    this.buildDots();
    this.page = Math.min(this.page, this.pages - 1);
    this.setActive(this.page);
  }

  buildDots() {
    if (!this.dotsWrap) return;
    this.dotsWrap.innerHTML = '';
    for (let i = 0; i < this.pages; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'zu-reviews__dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Go to review group ${i + 1}`);
      dot.addEventListener('click', () => this.goTo(i, true));
      this.dotsWrap.appendChild(dot);
    }
    this.dots = Array.from(this.dotsWrap.children);
  }

  goTo(page, fromUser) {
    this.page = (page + this.pages) % this.pages;
    this.viewport.scrollTo({ left: this.page * this.viewport.clientWidth, behavior: 'smooth' });
    this.setActive(this.page);
    if (fromUser) this.restart();
  }

  setActive(page) {
    if (!this.dots) return;
    this.dots.forEach((d, i) => d.classList.toggle('zu-reviews__dot--active', i === page));
  }

  bind() {
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.goTo(this.page - 1, true));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.goTo(this.page + 1, true));

    // Keep dots in sync with manual / touch scrolling.
    let raf;
    this.viewport.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const p = Math.round(this.viewport.scrollLeft / this.viewport.clientWidth);
        if (p !== this.page) {
          this.page = p;
          this.setActive(p);
        }
      });
    });

    ['mouseenter', 'focusin', 'touchstart'].forEach((e) =>
      this.addEventListener(e, () => this.stop(), { passive: true })
    );
    ['mouseleave', 'focusout'].forEach((e) => this.addEventListener(e, () => this.start()));

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.layout();
        this.viewport.scrollTo({ left: this.page * this.viewport.clientWidth });
      }, 150);
    });
  }

  start() {
    if (!this.interval || this.pages <= 1 || this.timer) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.timer = setInterval(() => this.goTo(this.page + 1, false), this.interval);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }

  restart() {
    this.stop();
    this.start();
  }
}

if (!customElements.get('zu-reviews-carousel')) {
  customElements.define('zu-reviews-carousel', ZuReviewsCarousel);
}
